import { useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { z } from 'zod';
import {
  safeValidateAddress,
  toChecksumAddress,
} from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FullscreenDialog } from '@/components/ui/fullscreen-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { useInviteMembers } from '@/hooks/admin/member/use-invite-members';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useApiKeyInfo } from '@/hooks/admin/organization/use-api-key-info';

// Tab button for member type selection
function MemberTypeTab({
  children,
  isActive,
  onClick,
}: {
  children: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className={cn(
        'px-3 py-1.5 rounded-full font-medium text-sm leading-5 transition-colors',
        isActive
          ? 'bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white'
          : 'bg-transparent text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

type RegularMember = {
  id: string;
  type: 'regular';
  firstName: string;
  lastName: string;
  email: string;
  error?: string;
};

type ApiMember = {
  id: string;
  type: 'api';
  name: string;
  walletAddress: string;
  error?: string;
};

// Discriminated union for member types
type NewMember = RegularMember | ApiMember;

const NAME_SCHEMA = z
  .string()
  .trim()
  .min(1)
  .refine((value) => !/[<>]/.test(value), {
    message: 'Name contains invalid characters',
  });
const EMAIL_SCHEMA = z.email();

function isValidName(value: string): boolean {
  return NAME_SCHEMA.safeParse(value).success;
}

function isValidEmail(value: string): boolean {
  return EMAIL_SCHEMA.safeParse(value.trim()).success;
}

function getNameError(value: string): string | undefined {
  const trimmedValue = value.trim();
  if (!trimmedValue) return undefined;

  const result = NAME_SCHEMA.safeParse(value);
  if (result.success) return undefined;

  return result.error.issues[0]?.message ?? 'Name contains invalid characters';
}

// Helper to create a new regular member
const createRegularMember = (): RegularMember => ({
  id: `member-${Date.now()}`,
  type: 'regular',
  firstName: '',
  lastName: '',
  email: '',
});

// Helper to create a new API member
const createApiMember = (): ApiMember => ({
  id: `member-${Date.now()}`,
  type: 'api',
  name: '',
  walletAddress: '',
});

// Helper to find duplicate wallet addresses among API members
// Returns a Set of checksummed addresses that appear more than once
const findDuplicateWalletAddresses = (members: NewMember[]): Set<string> => {
  const apiMembers = members.filter(
    (m): m is ApiMember => m.type === 'api' && m.walletAddress.trim() !== ''
  );

  // Get checksummed addresses for valid wallet addresses
  const checksumAddresses = apiMembers
    .map((m) => safeValidateAddress(m.walletAddress).checksumAddress)
    .filter((addr) => addr !== undefined);

  // Find duplicates by counting occurrences
  const addressCounts = new Map<string, number>();
  for (const addr of checksumAddresses) {
    addressCounts.set(addr, (addressCounts.get(addr) ?? 0) + 1);
  }

  // Return addresses that appear more than once
  const duplicates = new Set<string>();
  for (const [addr, count] of addressCounts) {
    if (count > 1) {
      duplicates.add(addr);
    }
  }
  return duplicates;
};

interface NewMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteSuccess: () => void;
}

export function NewMemberDialog({
  open,
  onOpenChange,
  onInviteSuccess,
}: NewMemberDialogProps) {
  // Initialize state with a single regular member as the default member entry for the invite dialog
  const [members, setMembers] = useState<NewMember[]>([createRegularMember()]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const { organization } = useMyOrganization();
  const { inviteMembers, isInviting } = useInviteMembers();
  const { apiKeyInfo } = useApiKeyInfo(organization?.id ?? null);

  // Check if org has API key to show the toggle
  const canAddApiMembers = apiKeyInfo.exists;

  const addMember = () => {
    setMembers((prev) => [...prev, createRegularMember()]);
  };

  const removeMember = (id: string) => {
    if (members.length > 1) {
      setMembers((prev) => prev.filter((member) => member.id !== id));
    }
  };

  // Toggle member type between regular and API
  const toggleMemberType = (id: string) => {
    setMembers((prev) =>
      prev.map((member) => {
        // Case: not the member toggled, return unchanged
        if (member.id !== id) return member;
        // Case: toggle specific member from regular to api or vice versa
        if (member.type === 'regular') {
          return createApiMember();
        } else {
          return createRegularMember();
        }
      })
    );
  };

  // Update a regular member field
  const updateRegularMember = (
    id: string,
    field: keyof Omit<RegularMember, 'id' | 'type' | 'error'>,
    value: string
  ) => {
    setMembers((prev) =>
      prev.map((member) => {
        // Case: not the member to update, return unchanged
        if (member.id !== id || member.type !== 'regular') return member;
        // Case: update the member's field
        const updated = { ...member, [field]: value };
        // Clear error when email field changes
        if (field === 'email') {
          delete updated.error;
        }
        return updated;
      })
    );
  };

  const trimRegularMemberEmail = (id: string) => {
    setMembers((prev) =>
      prev.map((member) => {
        if (member.id !== id || member.type !== 'regular') return member;
        return {
          ...member,
          email: member.email.trim(),
        };
      })
    );
  };

  const trimRegularMemberName = (
    id: string,
    field: 'firstName' | 'lastName'
  ) => {
    setMembers((prev) =>
      prev.map((member) => {
        if (member.id !== id || member.type !== 'regular') return member;
        return {
          ...member,
          [field]: member[field].trim(),
        };
      })
    );
  };

  // Update an API member field
  const updateApiMember = (
    id: string,
    field: keyof Omit<ApiMember, 'id' | 'type' | 'error'>,
    value: string
  ) => {
    setMembers((prev) =>
      prev.map((member) => {
        // Case: not the member to update, return unchanged
        if (member.id !== id || member.type !== 'api') return member;
        // Case: update the member's field
        const updated = { ...member, [field]: value };
        // Clear error when walletAddress field changes
        if (field === 'walletAddress') {
          delete updated.error;
        }
        return updated;
      })
    );
  };

  const trimApiMemberName = (id: string) => {
    setMembers((prev) =>
      prev.map((member) => {
        if (member.id !== id || member.type !== 'api') return member;
        return {
          ...member,
          name: member.name.trim(),
        };
      })
    );
  };

  // Validate a single member
  const isMemberValid = (member: NewMember): boolean => {
    // Case: regular member, check name and email
    if (member.type === 'regular') {
      return (
        isValidName(member.firstName) &&
        isValidName(member.lastName) &&
        member.email.trim() !== '' &&
        isValidEmail(member.email)
      );
    }
    // Case: api member, check name and wallet address
    else {
      return (
        isValidName(member.name) &&
        safeValidateAddress(member.walletAddress).isValid
      );
    }
  };

  const getRegularMemberEmailError = (
    member: RegularMember
  ): string | undefined => {
    if (member.error) return member.error;
    if (member.email.trim() && !isValidEmail(member.email)) {
      return 'Enter a valid email address';
    }
    return undefined;
  };

  const getRegularMemberFirstNameError = (
    member: RegularMember
  ): string | undefined => getNameError(member.firstName);

  const getRegularMemberLastNameError = (
    member: RegularMember
  ): string | undefined => getNameError(member.lastName);

  const getApiMemberNameError = (member: ApiMember): string | undefined =>
    getNameError(member.name);

  // Check for duplicate wallet addresses among API members
  const duplicateWalletAddresses = findDuplicateWalletAddresses(members);

  // Validate the entire form
  const isFormValid = () => {
    // Form is invalid if there are duplicate wallet addresses
    if (duplicateWalletAddresses.size > 0) {
      return false;
    }
    return members.every(isMemberValid);
  };

  const handleSubmit = () => {
    if (!isFormValid()) {
      return;
    }

    // Show confirmation dialog instead of immediately creating the members
    setShowConfirmationDialog(true);
  };

  const handleConfirmation = async () => {
    if (!organization) return;

    // Separate regular members and API members
    const regularMembers = members.filter(
      (m): m is RegularMember => m.type === 'regular'
    );
    const apiMembersData = members.filter(
      (m): m is ApiMember => m.type === 'api'
    );

    // Prepare request data
    const emails = regularMembers
      .map((m) => m.email.trim())
      .filter((e) => e.length > 0);

    const apiMembers = apiMembersData
      .filter((m) => isValidName(m.name) && m.walletAddress.trim())
      .map((m) => ({
        name: m.name.trim(),
        walletAddress: toChecksumAddress(m.walletAddress.trim()), // Checksum the address
      }));

    const results = await inviteMembers({
      emails,
      apiMembers,
    });

    // Map errors back to the members
    const updatedMembers = members.map((member) => {
      // Case: regular member, match by email
      if (member.type === 'regular') {
        const result = results.find((r) => r.email === member.email.trim());
        if (result && !result.success) {
          return { ...member, error: result.cause };
        }
        // Remove error if it succeeded
        const { error: _error, ...memberWithoutError } = member;
        return memberWithoutError;
      }
      // Case: api member, match by wallet address
      else {
        // API member error matching:
        // The server returns different values in the `email` field depending on success/failure:
        // - On FAILURE: returns the wallet address (eoaAddress) as the identifier
        // - On SUCCESS: returns a fake email like "{userId}@noemail.fake"
        //
        // We match by checking if the result's email contains the wallet address.
        // This will find failed results (where email = wallet address).
        // For successes, no match is found, which is fine - we just clear any previous error.
        const checksumAddress = safeValidateAddress(
          member.walletAddress
        ).checksumAddress;
        const result = results.find((r) =>
          r.email.includes(checksumAddress ?? '')
        );
        if (result && !result.success) {
          return { ...member, error: result.cause };
        }
        // No matching error found - remove any previous error
        const { error: _error, ...memberWithoutError } = member;
        return memberWithoutError;
      }
    });

    // Check if there were any failures
    const hasErrors = results.some((r) => !r.success);

    if (hasErrors) {
      // Keep dialog open and show errors
      setMembers(updatedMembers);
      setShowConfirmationDialog(false);
    } else {
      // All succeeded - reset form and close both dialogs
      setMembers([createRegularMember()]);
      // Refresh the invites list
      onInviteSuccess();
      setShowConfirmationDialog(false);
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    // Reset form and close confirmation dialog if open
    setMembers([createRegularMember()]);
    setShowConfirmationDialog(false);
    onOpenChange(false);
  };

  // const validMemberCount = members.filter(
  //   (member) =>
  //     member.firstName.trim() && member.lastName.trim() && member.email.trim()
  // ).length;

  return (
    <>
      <FullscreenDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Add new members"
        footer={
          <div className="flex justify-center gap-2 max-w-xl w-full">
            <Button size="lg" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="lg" onClick={handleSubmit} disabled={!isFormValid()}>
              Add {members.length} Member{members.length > 1 ? 's' : ''}
            </Button>
          </div>
        }
        bodyContainerClassName="bg-muted"
      >
        <div className="max-w-lg mx-auto">
          {/* Members Section */}
          <div>
            <div className="flex flex-col gap-5">
              {members.map((member) => {
                const walletValidation =
                  member.type === 'api'
                    ? safeValidateAddress(member.walletAddress)
                    : null;
                const regularMemberEmailError =
                  member.type === 'regular'
                    ? getRegularMemberEmailError(member)
                    : undefined;
                const regularMemberFirstNameError =
                  member.type === 'regular'
                    ? getRegularMemberFirstNameError(member)
                    : undefined;
                const regularMemberLastNameError =
                  member.type === 'regular'
                    ? getRegularMemberLastNameError(member)
                    : undefined;
                const apiMemberNameError =
                  member.type === 'api'
                    ? getApiMemberNameError(member)
                    : undefined;

                return (
                  <Card
                    key={member.id}
                    className="p-5 relative bg-zinc-50 border-zinc-200"
                  >
                    <div className="flex flex-col gap-5">
                      {/* Member Type Tabs - only show if org has API key */}
                      {canAddApiMembers && (
                        <div className="bg-zinc-100 dark:bg-zinc-800 border border-solid border-zinc-200 dark:border-zinc-700 flex items-center p-1 rounded-full w-fit">
                          <MemberTypeTab
                            isActive={member.type === 'regular'}
                            onClick={() => {
                              if (member.type !== 'regular') {
                                toggleMemberType(member.id);
                              }
                            }}
                          >
                            Regular Member
                          </MemberTypeTab>
                          <MemberTypeTab
                            isActive={member.type === 'api'}
                            onClick={() => {
                              if (member.type !== 'api') {
                                toggleMemberType(member.id);
                              }
                            }}
                          >
                            API Member
                          </MemberTypeTab>
                        </div>
                      )}

                      {/* Regular Member Fields */}
                      {member.type === 'regular' && (
                        <>
                          <div className="flex flex-row gap-5">
                            <Input
                              placeholder="First name"
                              value={member.firstName}
                              onChange={(e) =>
                                updateRegularMember(
                                  member.id,
                                  'firstName',
                                  e.target.value
                                )
                              }
                              onBlur={() =>
                                trimRegularMemberName(member.id, 'firstName')
                              }
                              size="lg"
                              className={cn(
                                'bg-background',
                                regularMemberFirstNameError &&
                                  'border-red-500 focus-visible:ring-red-500'
                              )}
                            />
                            <Input
                              placeholder="Last name"
                              value={member.lastName}
                              onChange={(e) =>
                                updateRegularMember(
                                  member.id,
                                  'lastName',
                                  e.target.value
                                )
                              }
                              onBlur={() =>
                                trimRegularMemberName(member.id, 'lastName')
                              }
                              size="lg"
                              className={cn(
                                'bg-background',
                                regularMemberLastNameError &&
                                  'border-red-500 focus-visible:ring-red-500'
                              )}
                            />
                          </div>
                          {(regularMemberFirstNameError ||
                            regularMemberLastNameError) && (
                            <p className="text-sm text-red-600 -mt-3">
                              {regularMemberFirstNameError ??
                                regularMemberLastNameError}
                            </p>
                          )}

                          <div>
                            <Input
                              type="email"
                              placeholder="Email address"
                              value={member.email}
                              onChange={(e) =>
                                updateRegularMember(
                                  member.id,
                                  'email',
                                  e.target.value
                                )
                              }
                              onBlur={() => trimRegularMemberEmail(member.id)}
                              size="lg"
                              className={cn(
                                'bg-background',
                                regularMemberEmailError &&
                                  'border-red-500 focus-visible:ring-red-500'
                              )}
                            />
                            {regularMemberEmailError && (
                              <p className="text-sm text-red-600 mt-1.5">
                                {regularMemberEmailError}
                              </p>
                            )}
                          </div>
                        </>
                      )}

                      {/* API Member Fields */}
                      {member.type === 'api' && (
                        <>
                          <Input
                            placeholder="Name"
                            value={member.name}
                            onChange={(e) =>
                              updateApiMember(member.id, 'name', e.target.value)
                            }
                            onBlur={() => trimApiMemberName(member.id)}
                            size="lg"
                            className={cn(
                              'bg-background',
                              apiMemberNameError &&
                                'border-red-500 focus-visible:ring-red-500'
                            )}
                          />
                          {apiMemberNameError && (
                            <p className="text-sm text-red-600 -mt-3">
                              {apiMemberNameError}
                            </p>
                          )}

                          <div>
                            <Input
                              placeholder="Wallet address (0x...)"
                              value={member.walletAddress}
                              onChange={(e) =>
                                updateApiMember(
                                  member.id,
                                  'walletAddress',
                                  e.target.value
                                )
                              }
                              size="lg"
                              className={cn(
                                'bg-background font-mono',
                                (member.error ||
                                  (member.walletAddress &&
                                    !walletValidation?.isValid) ||
                                  (walletValidation?.checksumAddress &&
                                    duplicateWalletAddresses.has(
                                      walletValidation.checksumAddress
                                    ))) &&
                                  'border-red-500 focus-visible:ring-red-500'
                              )}
                            />
                            {/* Show checksum hint if address is valid but not checksummed */}
                            {walletValidation?.checksumAddress &&
                              walletValidation.checksumAddress !==
                                member.walletAddress && (
                                <p className="text-xs text-muted-foreground mt-1.5 px-1">
                                  Checksummed:{' '}
                                  <span className="font-mono">
                                    {walletValidation.checksumAddress}
                                  </span>
                                </p>
                              )}
                            {/* Show validation error */}
                            {member.walletAddress &&
                              !walletValidation?.isValid && (
                                <p className="text-sm text-red-600 mt-1.5">
                                  Enter a valid wallet address
                                </p>
                              )}
                            {/* Show duplicate wallet address error */}
                            {walletValidation?.checksumAddress &&
                              duplicateWalletAddresses.has(
                                walletValidation.checksumAddress
                              ) && (
                                <p className="text-sm text-red-600 mt-1.5">
                                  Duplicate wallet address
                                </p>
                              )}
                            {/* Show server error */}
                            {member.error && (
                              <p className="text-sm text-red-600 mt-1.5">
                                {member.error}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    {members.length > 1 && (
                      <Button
                        size={'icon'}
                        variant={'ghost'}
                        className={cn(
                          `h-8 w-8 p-0  hover:bg-red-300 hover:text-red-800`,
                          'absolute top-7 -right-11'
                        )}
                        onClick={() => removeMember(member.id)}
                      >
                        <X className="h-8 w-8" />
                      </Button>
                    )}
                  </Card>
                );
              })}

              {/* Add Another Member Button */}
              <Button
                variant="outline"
                onClick={addMember}
                className="w-full justify-center rounded-xl bg-transparent border border-border
                hover:bg-background h-[100px] text-zinc-500 "
              >
                <Plus className="mr-2 h-4 w-4" />
                Add another member
              </Button>
            </div>
          </div>
        </div>
      </FullscreenDialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmationDialog} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Invites sent to new members</DialogTitle>
            <DialogDescription>
              New members must set up their account and mobile app before admins
              can provide final approval.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={handleConfirmation}
              disabled={isInviting}
              className="relative"
            >
              <span className={isInviting ? 'invisible' : ''}>Okay</span>
              {isInviting ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </span>
              ) : null}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
